import { ShopwareProduct, ShopwareUuid } from '@services/types/Shopware';
import { CartApiResponse } from '@pages/api/cart/add-bulk-items';

type NewsletterSourcesEnum = 'order' | 'event' | 'footer' | 'popup' | 'start';

interface SubscribeParams {
    email: string;
    country: string;
    store: ShopwareUuid;
    source: NewsletterSourcesEnum;
    tags?: string|null|undefined;
}

class StorefrontApiService {
    basePath: string;

    constructor() {
        this.basePath = '';
    }

    getHeaders() {
        return {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
        };
    }

    apiPath(path: string) {
        return `${this.basePath}/api/${path}`;
    }

    async get(path: string) {
        let response = await fetch(this.apiPath(path), {
            method: 'GET',
            headers: this.getHeaders(),
            credentials: 'include',
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return await response.json();
    }

    async delete(path: string) {
        let response = await fetch(this.apiPath(path), {
            method: 'DELETE',
            headers: this.getHeaders(),
            credentials: 'include',
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return response;
    }

    async post(path: string, data: any) {
        let response = await fetch(this.apiPath(path), {
            method: 'POST',
            headers: this.getHeaders(),
            credentials: 'include',
            body: JSON.stringify(data),
        });

        if (response.status >= 400) {
            return { status: 'error', data: null, response: response };
        }

        return await response.json();
    }

    async getProductsElevated(productIds: Array<string>) {
        return this.get(`product/elevated?ids=${productIds.join(',')}`);
    }

    async getProductBySkus(skus: number[]) {
        return this.get(`product/get/by-skus?skus=${skus.join(',')}`);
    }

    async getProductById(productId: string) {
        return this.get(`product/get/by-id/${productId}`);
    }

    async getProductByIds(productIds: Array<string>): Promise<{ products: ShopwareProduct[], status: string }> {
        // @ts-ignore
        return this.get(`product/get/by-ids?ids=${productIds.map((p) => p.replaceAll('-', '')).join(',')}`);
    }

    async fetchStaticLabels() {
        return this.get('properties/labels');
    }

    async getVariants(parentId: string) {
        return this.get(`product/get/variants/${parentId}`);
    }

    async addItemsToCart(productIds: Array<string>): Promise<CartApiResponse> {
        return this.post('cart/add-items', {
            productIds,
        });
    }

    async addProductsToCart(products: Array<{ productId: ShopwareUuid, quantity: number }>): Promise<CartApiResponse> {
        return this.post('cart/add-bulk-items', {
            products,
        });
    }

    async subscribeToNewsletter(payload: SubscribeParams) {
        return this.post('voyado/newsletter', payload);
    }
}

export default StorefrontApiService;
